import { ChevronDown, X } from "lucide-react";

import { STAGE_LIST } from "@/lib/domain-constants";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export type ReportSort = "publicatedAt" | "amount";

export interface ErrorFiltersValue {
  stages: string[];
  publicatedFrom: string; // YYYY-MM-DD
  publicatedTo: string; // YYYY-MM-DD
  sortBy: ReportSort;
}

export const EMPTY_ERROR_FILTERS: ErrorFiltersValue = {
  stages: [],
  publicatedFrom: "",
  publicatedTo: "",
  sortBy: "publicatedAt",
};

function StageSelect({
  selected,
  onChange,
}: {
  selected: string[];
  onChange: (stages: string[]) => void;
}) {
  const label =
    selected.length === 0
      ? "Todos los estados"
      : selected.length === 1
        ? (STAGE_LIST.find((s) => s.key === selected[0])?.label ?? "1 estado")
        : `${selected.length} estados`;

  function toggle(key: string) {
    onChange(
      selected.includes(key)
        ? selected.filter((s) => s !== key)
        : [...selected, key],
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-full justify-between font-normal sm:w-[220px]",
            selected.length > 0 && "border-primary/50 text-primary",
          )}
        >
          {label}
          <ChevronDown className="size-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[260px] p-2">
        <div className="max-h-[320px] overflow-y-auto">
          {STAGE_LIST.map((stage) => (
            <label
              key={stage.key}
              className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-accent"
            >
              <Checkbox
                checked={selected.includes(stage.key)}
                onCheckedChange={() => toggle(stage.key)}
              />
              {stage.label}
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** Filtros de los reportes admin: estado, rango de publicación y orden. */
export function ContractingErrorFilters({
  value,
  onChange,
}: {
  value: ErrorFiltersValue;
  onChange: (value: ErrorFiltersValue) => void;
}) {
  const hasFilters =
    value.stages.length > 0 || !!value.publicatedFrom || !!value.publicatedTo;

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
      <div className="flex flex-col gap-1.5">
        <Label className="text-xs text-muted-foreground">Estado</Label>
        <StageSelect
          selected={value.stages}
          onChange={(stages) => onChange({ ...value, stages })}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="from" className="text-xs text-muted-foreground">
          Publicadas desde
        </Label>
        <Input
          id="from"
          type="date"
          className="sm:w-[170px]"
          value={value.publicatedFrom}
          onChange={(e) =>
            onChange({ ...value, publicatedFrom: e.target.value })
          }
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="to" className="text-xs text-muted-foreground">
          Hasta
        </Label>
        <Input
          id="to"
          type="date"
          className="sm:w-[170px]"
          value={value.publicatedTo}
          onChange={(e) => onChange({ ...value, publicatedTo: e.target.value })}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label className="text-xs text-muted-foreground">Ordenar por</Label>
        <Select
          value={value.sortBy}
          onValueChange={(sortBy) =>
            onChange({ ...value, sortBy: sortBy as ReportSort })
          }
        >
          <SelectTrigger className="sm:w-[190px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="publicatedAt">Fecha de publicación</SelectItem>
            <SelectItem value="amount">Monto</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {hasFilters && (
        <Button
          variant="ghost"
          className="text-muted-foreground"
          onClick={() =>
            onChange({ ...EMPTY_ERROR_FILTERS, sortBy: value.sortBy })
          }
        >
          <X className="size-4" />
          Limpiar
        </Button>
      )}
    </div>
  );
}
